/* import __COLOCATED_TEMPLATE__ from './index.hbs'; */
import Component from '@glimmer/component';
import {
  DELIVERY_METRICS_MEASURES,
  JOURNEY_METRICS_MEASURES,
  MEASURE_LABELS,
  SimpleCampaignNewsletter,
} from 'cio/utils/metrics/analysis';
import { Measure, Periods, getGroupingRanges } from 'cio/utils/metrics/common';
import { findDenominator, measureDenominators, MeasureType } from 'cio/utils/metrics/measure';
import { hash, Task } from 'ember-concurrency';
import { task } from 'ember-concurrency';
import { action, computed } from '@ember/object';
import moment from 'moment-timezone';
import { tracked } from '@glimmer/tracking';
import { isNone, isPresent } from '@ember/utils';
import { JourneyMetricsMeasure, monthsBetweenDates } from 'cio/utils/journey-metrics';
import merge from 'lodash.merge';
import { addDashedZonesToIncompleteDataPoints } from 'cio/utils/graphs';
import { METRIC_COLORS } from 'cio/utils/metrics/colors';
import { Grouping } from 'cio/utils/metrics/resolution';
import { deliveryStatesAndActionsPerType } from 'cio/utils/action-info';
import type { Options } from 'highcharts';
import { TimeRange } from 'cio/utils/time';
import { inject as service } from '@ember/service';
import SettingsService from 'cio/services/settings';
import WorkersService, { CIOWorker } from 'cio/services/workers';

interface AnalysisPageChartArgs {
  selectedMeasures: string[];
  onSelectedMeasuresChanged: (selectedMeasures: string[]) => unknown;
  selectedChannels: string[];
  allCampaignsAndNewsletters: SimpleCampaignNewsletter[];
  refinedCampaignAndNewsletterIds: string[];
  selectedChartCampaignAndNewsletterIds: string[];
  onSelectedChartCampaignAndNewsletterIdsChange: (ids: string[]) => unknown;
  campaignAndNewsletterCount: number;
  sumMetrics: Task<unknown, []>;
  totalMetrics: Task<unknown, []>;
  totalMetric: any;
  rawSummedMetrics:
    | {
        _id: string;
        id: string;
        name: string;
        type: string;
        icon: string;
        data: {
          delivery_metrics: Record<string, number[] | number>;
          journey_metrics: Record<string, number[] | number>;
        };
      }[]
    | null;
  timeRange: TimeRange;
  timePeriod: Periods;
  chartType: 'individual' | 'aggregate';
  onChartTypeChange: (type: 'individual' | 'aggregate') => unknown;
  chartUnit: 'count' | 'percentage';
  onChartUnitChange: (unit: 'count' | 'percentage') => unknown;
  chartGrouping: Grouping;
  onChartGroupingChange: (grouping: Grouping, track?: boolean) => unknown;
  chartIndividualMeasure: string;
  onChartIndividualMeasureChange: (measure: string) => unknown;
  trackChartHover: (time: number) => unknown;
  rangeStartBeforeJourneyMetricsAvailability: boolean;
}

const BASE_CHART_OPTIONS: Options = {
  chart: {
    height: 286,
  },
  legend: {
    enabled: false,
  },
  time: {
    timezone: 'Etc/UTC',
  },
  xAxis: {
    type: 'datetime',
    startOfWeek: 1,
  },
  plotOptions: {
    series: {
      zoneAxis: 'x',
      zones: [],
    },
  },
  tooltip: {
    outside: true,
  },
};

export default class AnalysisPageChart extends Component<AnalysisPageChartArgs> {
  @service settings!: SettingsService;
  @service workers!: WorkersService;

  metrics = [
    ...[
      'sent',
      'delivered',
      'opened',
      'clicked',
      'clicked_to_opened',
      'converted',
      'unsubscribed',
      'topic_unsubscribed',
      'spammed',
      'failed',
      'bounced',
      'suppressed',
    ].map((measure, i) => {
      let value = `delivery_metrics.${measure}`;
      return {
        label: (MEASURE_LABELS as Record<string, string>)[value],
        measureType: 'delivery_metrics',
        measureName: measure,
        color: METRIC_COLORS[i % METRIC_COLORS.length],
        value,
      };
    }),
    ...['activated', 'in_workflow', 'converted', 'exited_early', 'finished'].map((measure, i) => {
      let value = `journey_metrics.${measure}`;
      return {
        label: (MEASURE_LABELS as Record<string, string>)[value],
        measureType: 'journey_metrics',
        measureName: measure,
        color: METRIC_COLORS[i % METRIC_COLORS.length],
        value,
      };
    }),
  ];

  get timeGroupings() {
    let isMonthlyTimePeriod = this.args.timePeriod === Periods.MONTHS;

    let timeGroupings = [{ label: 'Daily', value: Grouping.Daily, disabled: isMonthlyTimePeriod }];

    let { timeRange } = this.args;

    let daysInRange = (timeRange.end?.diff(timeRange.start, 'days') ?? 0) + 1;

    if (daysInRange >= 7) {
      timeGroupings.push({ label: 'Weekly', value: Grouping.Weekly, disabled: isMonthlyTimePeriod });
    }

    if (daysInRange >= 62) {
      timeGroupings.push({ label: 'Monthly', value: Grouping.Monthly, disabled: false });
    }

    return timeGroupings;
  }

  @tracked groupedMetrics:
    | {
        _id: string;
        id: string;
        name: string;
        type: string;
        icon: string;
        data: {
          per_channel_metrics: Record<string, number[] | number>;
          delivery_metrics: Record<string, number[] | number>;
          journey_metrics: Record<string, number[] | number>;
        };
      }[]
    | null = null;
  @tracked groupedTotalMetric: {
    per_channel_metrics: Record<string, number[] | number>;
    delivery_metrics: Record<string, number[] | number>;
    journey_metrics: Record<string, number[] | number>;
  } | null = null;

  constructor(owner: unknown, args: AnalysisPageChartArgs) {
    super(owner, args);

    if (this.args.timePeriod === Periods.MONTHS) {
      // If the timePeriod is "Last 12 months", select the monthly grouping. The
      // grouping select component will also get disabled, since we only have
      // the 12 months of data in this scenario
      this.args.onChartGroupingChange(Grouping.Monthly, false);
    } else if (!this.timeGroupings.some((grouping) => grouping.value === this.args.chartGrouping)) {
      // If the current time grouping doesn't exist as an option anymore (for
      // example, after rerunning the report with a different range), select the
      // default daily grouping again
      //
      // Second argument controls whether we want to track the change in
      // mixpanel. In this case we don't, since it's not the user having made
      // the change
      this.args.onChartGroupingChange(Grouping.Daily, false);
    }

    this.groupMetrics.perform();
  }

  groupMetrics = task({ restartable: true }, async () => {
    let rangeLengths = getGroupingRanges(
      this.args.timeRange,
      this.args.timePeriod === Periods.MONTHS ? this.args.timePeriod : Periods.DAYS,
      this.args.chartGrouping,
    );
    let groupWorker = this.workers.getWorker(CIOWorker.TimeGroupAnalysisMetrics);

    let results = await hash({
      metrics: groupWorker.postMessage([
        this.args.rawSummedMetrics,
        this.args.chartGrouping,
        rangeLengths,
        this.args.selectedChannels,
        {
          DELIVERY_METRICS_MEASURES,
          JOURNEY_METRICS_MEASURES,
          DELIVERY_METRICS_MEASURE_DENOMINATORS: measureDenominators(MeasureType.DeliveryMetrics),
          JOURNEY_METRICS_MEASURE_DENOMINATORS: measureDenominators(MeasureType.JourneyMetrics),
          METRICS_PER_ACTION_TYPE: deliveryStatesAndActionsPerType(true),
        },
      ]),
      total: groupWorker.postMessage([
        [{ data: this.args.totalMetric }],
        this.args.chartGrouping,
        rangeLengths,
        this.args.selectedChannels,
        {
          DELIVERY_METRICS_MEASURES,
          JOURNEY_METRICS_MEASURES,
          DELIVERY_METRICS_MEASURE_DENOMINATORS: measureDenominators(MeasureType.DeliveryMetrics),
          JOURNEY_METRICS_MEASURE_DENOMINATORS: measureDenominators(MeasureType.JourneyMetrics),
          METRICS_PER_ACTION_TYPE: deliveryStatesAndActionsPerType(true),
        },
      ]),
    });

    this.groupedMetrics = results.metrics;
    this.groupedTotalMetric = results.total[0].data;
    this.setChartOptions();
  });

  get allCampaignsAndNewsletters() {
    return this.args.allCampaignsAndNewsletters
      .filter((c) => this.args.refinedCampaignAndNewsletterIds.includes(c.id))
      .map((c, i) => ({
        ...c,
        color: METRIC_COLORS[i % METRIC_COLORS.length],
      }));
  }

  @computed('args.{chartUnit,selectedChannels.[]}', 'metrics.@each.selected')
  get availableMetrics() {
    if (this.args.chartUnit === 'count') {
      return this.metrics;
    }

    return this.metrics.filter((m) => {
      let denominator = findDenominator(m.measureName as any, m.measureType as any);
      return denominator != null;
    });
  }

  @computed('metrics.[]', 'args.selectedMeasures.[]')
  get selectedMetrics() {
    return this.availableMetrics.filter((m) => this.args.selectedMeasures.includes(m.value));
  }

  get showUntrackedChangeAnnotation() {
    if (this.args.chartType === 'aggregate') {
      return isPresent(this.selectedMetrics.find((metric: any) => metric.measureName === 'clicked'));
    } else {
      return this.args.chartIndividualMeasure === 'delivery_metrics.clicked';
    }
  }

  get adjustedTimeRange() {
    let timeRange = {
      start: moment.tz(
        `${this.args.timeRange.start?.format('YYYY-MM-DD')} 00:00:00`,
        this.settings.environmentSettings.journeyMetricsTimezone,
      ),
      end: moment.tz(
        `${this.args.timeRange.end?.format('YYYY-MM-DD')} 00:00:00`,
        this.settings.environmentSettings.journeyMetricsTimezone,
      ),
    };

    if (this.args.chartGrouping === Grouping.Weekly) {
      let diffInWeeks = timeRange.end.diff(timeRange.start, 'weeks');

      return {
        start: moment(timeRange.start),
        end: moment(timeRange.start).add(diffInWeeks, 'weeks'),
      };
    }

    if (this.args.chartGrouping === Grouping.Monthly) {
      return {
        start: moment(timeRange.start).startOf('month'),
        end: moment(timeRange.end).startOf('month'),
      };
    }

    return timeRange;
  }

  get chartIndividualMeasureType() {
    let [measureType] = this.args.chartIndividualMeasure.split('.');
    return measureType;
  }

  @tracked chartOptions: Options = BASE_CHART_OPTIONS;

  @action
  setChartOptions() {
    let timeRange = {
      start: moment.tz(
        `${this.args.timeRange.start?.format('YYYY-MM-DD')} 00:00:00`,
        this.settings.environmentSettings.journeyMetricsTimezone,
      ),
      end: moment.tz(
        `${this.args.timeRange.end?.format('YYYY-MM-DD')} 00:00:00`,
        this.settings.environmentSettings.journeyMetricsTimezone,
      ),
    };

    let timestamps: number[] = [];
    if (this.args.chartGrouping === Grouping.Daily) {
      let start = moment(timeRange.start).unix();
      let steps = timeRange.end.diff(timeRange.start, 'days');
      timestamps = [start * 1000, ...[...new Array(steps)].map((_, i) => (start + (i + 1) * 24 * 60 * 60) * 1000)];
    } else if (this.args.chartGrouping === Grouping.Weekly) {
      let steps = timeRange.end.diff(timeRange.start, 'weeks');
      let start = timeRange.start.unix();
      timestamps = [start * 1000, ...[...new Array(steps)].map((_, i) => (start + (i + 1) * 24 * 60 * 60 * 7) * 1000)];
    } else if (this.args.chartGrouping === Grouping.Monthly) {
      timestamps = monthsBetweenDates(timeRange.start, timeRange.end).map(
        (dateString) =>
          moment.tz(dateString, this.settings.environmentSettings.journeyMetricsTimezone).startOf('month').unix() *
          1000,
      );
    }

    let series: any;
    if (this.args.chartType === 'aggregate') {
      series = this.selectedMetrics.map((m) => ({
        id: m.value,
        name: m.label,
        data: (this.groupedTotalMetric as any)[m.measureType][
          `${m.measureName}${this.args.chartUnit === 'percentage' ? '_rates' : ''}`
        ].map((value: number, i: number) => ({
          x: timestamps[i],
          y: this.args.chartUnit === 'count' ? value : value == null ? value : value * 100,
        })),
        colorIndex: m.color.name,
        type: 'line',
      }));
    } else {
      let [measureType, measureName] = this.args.chartIndividualMeasure.split('.');
      series = this.allCampaignsAndNewsletters
        .filter((c) => this.args.selectedChartCampaignAndNewsletterIds.includes(c.id))
        .map((c) => {
          let metric = (this.groupedMetrics ?? []).find((m) => m.id === c.id);

          if (isNone(metric)) {
            return {};
          }

          return {
            id: c.id,
            name: metric.name,
            data: (
              metric.data[measureType as 'delivery_metrics' | 'journey_metrics'][
                `${measureName}${this.args.chartUnit === 'percentage' ? '_rates' : ''}`
              ] as number[]
            ).map((value: number, i: number) => ({
              x: timestamps[i],
              y: this.args.chartUnit === 'count' ? value : value == null ? value : value * 100,
            })),
            colorIndex: c.color.name,
            type: 'line',
          };
        });
    }

    let options: Options = merge({}, BASE_CHART_OPTIONS, {
      series: series as any,
      time: {
        timezone: this.settings.environmentSettings.journeyMetricsTimezone,
      },
    });

    if (this.args.chartUnit === 'percentage') {
      options.yAxis = {
        labels: {
          format: '{value:.1f}%',
        },
      };
      options.tooltip = {
        valueDecimals: 1,
        valueSuffix: '%',
      };
    } else {
      // Need to explicitly reset these values, otherwise highcharts will keep
      // the percentage formatting when switching
      options.yAxis = {
        labels: {
          format: undefined,
        },
      };
      options.tooltip = {
        valueDecimals: undefined,
        valueSuffix: undefined,
      };
    }

    if (this.args.chartGrouping === Grouping.Weekly) {
      (options.xAxis as any).startOfWeek = this.adjustedTimeRange.start.day();
    }
    options = addDashedZonesToIncompleteDataPoints(options, timestamps, timeRange, this.args.chartGrouping);

    this.chartOptions = options;
  }

  @action
  onChartUnitChange(value: 'count' | 'percentage') {
    this.args.onChartUnitChange(value);

    if (value === 'percentage') {
      this.args.onSelectedMeasuresChanged(
        this.args.selectedMeasures.filter((m) => {
          let [type, name] = m.split('.');
          if (type === 'delivery_metrics') {
            return !isNone(findDenominator(name as Measure, MeasureType.DeliveryMetrics));
          } else if (type === 'journey_metrics') {
            return !isNone(findDenominator(name as JourneyMetricsMeasure, MeasureType.JourneyMetrics));
          }

          return false;
        }),
      );

      if (this.args.chartType === 'individual') {
        let [type, name] = this.args.chartIndividualMeasure.split('.');
        let denominator: string | null = null;
        if (type === 'delivery_metrics') {
          denominator = findDenominator(name as Measure, MeasureType.DeliveryMetrics);
        } else if (type === 'journey_metrics') {
          denominator = findDenominator(name as JourneyMetricsMeasure, MeasureType.JourneyMetrics);
        }
        if (isNone(denominator)) {
          this.args.onChartIndividualMeasureChange(this.availableMetrics[0].value);
        }
      }
    }

    this.setChartOptions();
  }

  @action
  onChartTypeChange(value: 'individual' | 'aggregate') {
    this.args.onChartTypeChange(value);
    this.setChartOptions();
  }

  @action
  onChartIndividualMeasureChange(value: string) {
    this.args.onChartIndividualMeasureChange(value);
    this.setChartOptions();
  }

  @action
  onChartGroupingChange(grouping: Grouping) {
    this.args.onChartGroupingChange(grouping);
    this.groupMetrics.perform();
  }

  @action
  toggleMetric(measureValue: string, checked: boolean) {
    if (checked) {
      this.args.onSelectedMeasuresChanged([...this.args.selectedMeasures, measureValue]);
    } else {
      this.args.onSelectedMeasuresChanged(this.args.selectedMeasures.filter((m) => m !== measureValue));
    }

    this.setChartOptions();
  }

  @action
  toggleCampaignOrNewsletter(id: string, checked: boolean) {
    if (checked) {
      this.args.onSelectedChartCampaignAndNewsletterIdsChange([...this.args.selectedChartCampaignAndNewsletterIds, id]);
    } else {
      this.args.onSelectedChartCampaignAndNewsletterIdsChange(
        this.args.selectedChartCampaignAndNewsletterIds.filter((m) => m !== id),
      );
    }

    this.setChartOptions();
  }
}
